<template>
  <div class="text-area">
    <cv-text-area
      v-if="visible"
      :theme="theme"
      :label="label"
      :model-value="inputValue"
      :disabled="disabled"
      :placeholder="placeholder"
      :helper-text="helperText"
      :invalid-message="invalidMessage"
      @paste="handlePaste"
      @change="handleChange"
      @update:model-value="handleInput"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown="handleKeydown"
      @keyup="handleKeyup"
    />

    <text-word-character-count v-if="showWordCounter" :text-content="inputValue" />
  </div>
</template>

<script setup lang="ts">
import TextMixin from "../mixin/text.mixin";
import TextWordCharacterCount from "@plugins/character-count/components/TextWordCharacterCount.vue";
import { ref, computed } from "vue";
import { useVm } from "@utils/vm.ts";

// Mixin
defineOptions({
  mixins: [TextMixin],
});

// Props
interface Props {
  enableWordCount?: boolean;
  disableEnterLineBreak?: boolean;
}

const { enableWordCount = false, disableEnterLineBreak = false } = defineProps<Props>();

const emit = defineEmits<{
  keyup: [event: KeyboardEvent];
}>();

// Composables
const { vm } = useVm();

// Data
const isFocus = ref(false);

// Computed
const showWordCounter = computed(() => {
  return isFocus.value && enableWordCount;
});

// Methods
const handleBlur = () => {
  isFocus.value = false;
};

const handleFocus = () => {
  isFocus.value = true;
};

const handleKeydown = (event: KeyboardEvent) => {
  if (disableEnterLineBreak && event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
  }
};

const handleKeyup = (event: KeyboardEvent) => {
  emit("keyup", event);
};

defineExpose({
  validateMaxLimit: () => vm.validateMaxLimit(),
  handleInput: (inputValue) => vm.handleInput(inputValue),
  handlePaste: (event) => vm.handlePaste(event),
  handleChange: () => vm.handleChange(),
});
</script>
